import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import Header from './header'
import CustomerHeader from '../customer/CustomerHeader'
import Footer from './Footer'
import Newsletter from './HomeNewsletter'
import { useCustomerAuth } from '../../contexts/CustomerAuthContext'
import { useTalentAuth } from '../../contexts/TalentAuthContext'
import VerifiedBadge from '../common/VerifiedBadge'
import MembershipTierBadge from '../common/MembershipTierBadge'
import ServiceMap from './ServiceMap'
import { Phone, AlertCircle } from 'lucide-react'
import api, { API_BASE_URL } from '../../utils/api';
import { api as talentApi } from '../../contexts/TalentAuthContext';
import Swal from 'sweetalert2';

// Types
interface WorkerService {
  id: number;
  title: string;
  image: string;
  price: number;
  duration: string;
  category: string;
}

interface Worker {
  id: number;
  name: string;
  firstName: string;
  lastName: string;
  profilePicture: string;
  coverImage: string;
  parish: string;
  city: string;
  state: string;
  country: string;
  rating: number;
  totalReviews: number;
  experienceYears: number;
  bio: string;
  hourlyRate: number;
  phone: string | null;
  email: string;
  address: string;
  latitude: number | null;
  longitude: number | null;
  totalJobsCompleted: number;
  verificationStatus: 'pending' | 'verified' | 'rejected';
  membershipTier: 'standard' | 'premium' | null;
  membershipTierDisplay: string;
  verifiedBadge: boolean;
  phoneVisible: boolean;
  services: WorkerService[];
}

interface Service {
  id: number;
  title: string;
  description: string;
  price: number;
  duration: string;
  category: string;
  image: string;
  galleryImages: string[];
  portfolio?: string[];
  createdAt: string;
  updatedAt: string;
  worker: Worker;
}

interface Review {
  id: number;
  rating: number;
  communicationRating?: number;
  punctualityRating?: number;
  costRating?: number;
  qualityRating?: number;
  reviewText: string;
  responseText?: string;
  reviewerName: string;
  reviewerPicture: string;
  reviewerType: string;
  isVerified: boolean;
  createdAt: string;
}

interface ReviewData {
  reviews: Review[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
  };
  distribution: { [key: number]: number };
  distributionPercentages: { [key: number]: number };
  categoryDistributions?: {
    communication: { [key: number]: number };
    punctuality: { [key: number]: number };
    cost: { [key: number]: number };
    quality: { [key: number]: number };
  };
  categoryAverages?: {
    communication: number;
    punctuality: number;
    cost: number;
    quality: number;
  };
  totalReviews: number;
}

function Detail() {
  const { isAuthenticated: isTalentAuthenticated, talent } = useTalentAuth();
  const { isAuthenticated: isCustomerAuthenticated, customer } = useCustomerAuth();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Determine which type of user is authenticated
  const isAuthenticated = isTalentAuthenticated || isCustomerAuthenticated;

  const [service, setService] = useState<Service | null>(null);

  // Debug: Add authentication debugging
  useEffect(() => {
    const token = localStorage.getItem('skillseek_customer_token');
    const adminToken = localStorage.getItem('skillseek_admin_token');
    console.log('Debug - Authentication state:', {
      isAuthenticated,
      tokenExists: !!token,
      tokenPreview: token ? token.substring(0, 20) + '...' : 'No token',
      adminTokenExists: !!adminToken,
      adminTokenPreview: adminToken ? adminToken.substring(0, 20) + '...' : 'No admin token'
    });
  }, [isAuthenticated]);

  // Helper function to normalize image URLs - prevents double URL concatenation
  const BASE_URL = (() => {
    if (process.env.REACT_APP_API_URL) {
      return process.env.REACT_APP_API_URL.replace(/\/api$/, '');
    }
    if (typeof window !== 'undefined') {
      const h = window.location.hostname;
      const isLocal =
        h === 'localhost' || h === '127.0.0.1' ||
        /^192\.168\./.test(h) || /^10\./.test(h) ||
        /^172\.(1[6-9]|2[0-9]|3[01])\./.test(h);
      if (isLocal) return `http://${h}:5000`;
    }
    return 'https://api.skillseek.co';
  })();

  const normalizeImageUrl = (imagePath: string | null | undefined, fallback: string): string => {
    if (!imagePath) return fallback;
    
    let cleanPath = imagePath;
    const uploadsIndex = cleanPath.indexOf('/uploads/');
    if (uploadsIndex !== -1 && (cleanPath.startsWith('http://') || cleanPath.startsWith('https://'))) {
      cleanPath = cleanPath.substring(uploadsIndex + 1);
    }
    
    if (cleanPath.startsWith('http://') || cleanPath.startsWith('https://')) return cleanPath;
    // Avoid double /uploads/uploads/
    if (cleanPath.startsWith('/uploads/') || cleanPath.startsWith('uploads/')) {
      return `${BASE_URL}/${cleanPath.replace(/^\//, '')}`;
    }
    return `${BASE_URL}/uploads/${cleanPath}`;
  };

  // Helper function to mask email
  const cleanEmail = (email: string): string => {
    if (!email) return '';
    const match = email.match(/\[([^\]]+)\]\(mailto:[^)]+\)/);
    if (match) return match[1];
    return email;
  };
  const maskEmail = (email: string): string => {
    if (!email) return '';
    const [localPart, domain] = email.split('@');
    if (!localPart || !domain) return email;


    // Show first 2-3 characters and mask the rest
    const visibleChars = Math.min(3, Math.floor(localPart.length / 2));
    const maskedLocal = localPart.substring(0, visibleChars) + 'XXXX';
    return `${maskedLocal}@${domain}`;
  };

  // Helper function to mask phone number
  const maskPhone = (phone: string): string => {
    if (!phone) return '';
    // Remove any non-digit characters for processing
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 4) return phone;

    // Show last 4 digits and mask the rest
    const visibleDigits = digits.slice(-4);
    const maskedPart = 'X'.repeat(Math.max(0, digits.length - 4));
    return maskedPart + visibleDigits;
  };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reviewData, setReviewData] = useState<ReviewData | null>(null);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewSubmitting, setReviewSubmitting] = useState(false);

  useEffect(() => {
    // Reset states when ID changes
    setService(null);
    setError(null);
    setLoading(true);

    fetchService();
    fetchReviews();
  }, [id]);

  const fetchService = async () => {
    try {
      setLoading(true);
      setError(null);

      // Make sure we have an ID
      if (!id) {
        throw new Error('Service ID is missing');
      }

      console.log(`Fetching service with ID: ${id}`);

      // Use talentApi for authenticated requests
      const response = await talentApi.get(`/services/${id}`);
      const data = response.data;

      console.log('Service data received:', data);

      if (data && data.success && data.service) {
        // Debug: Log the services array
        console.log('Worker services:', data.service.worker?.services);
        setService(data.service);

        // Track profile view
        try {
          await talentApi.post(`/talent/track-view/${data.service.worker.id}`);
          console.log('Profile view tracked');
        } catch (trackingError) {
          console.error('Error tracking profile view:', trackingError);
          // Don't stop the page from loading if tracking fails
        }
      } else {
        throw new Error(data?.message || 'Service not found');
      }
    } catch (err) {
      console.error('Error fetching service:', err);

      // Handle different types of errors
      if (err instanceof Error) {
        if (err.message.includes('404')) {
          setError(`Service not found. Redirecting to home page...`);
          // Redirect after a delay
          setTimeout(() => navigate('/'), 3000);
        } else if (err.message.includes('failed to fetch') || err.message.toLowerCase().includes('network')) {
          setError('Network error. Please check your connection and try again.');
        } else {
          setError(`Error: ${err.message}`);
        }
      } else {
        setError('An unknown error occurred. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const [showContactModal, setShowContactModal] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [messageStatus, setMessageStatus] = useState({ type: '', text: '' });

  const handleContactRequest = async () => {
    // Track contact click
    if (service?.worker?.id) {
      try {
        await talentApi.post(`/talent/track-contact/${service.worker.id}`, {
          contactType: 'button'
        });
        console.log('Contact click tracked');
      } catch (trackingError) {
        console.error('Error tracking contact click:', trackingError);
      }
    }

    if (isAuthenticated) {
      // Show contact options modal
      setShowContactModal(true);
    } else {
      // Navigate to customer login with return URL
      navigate('/login', { state: { returnUrl: `/detail/${id}` } });
    }
  };

  const handleRestrictedLocationAction = () => {
    Swal.fire({
      title: 'Sign In Required',
      text: "Please sign in to access directions to this professional's location.",
      icon: 'info',
      showCancelButton: true,
      confirmButtonColor: '#3B82F6',
      cancelButtonColor: '#6B7280',
      confirmButtonText: 'Sign In',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        navigate('/login', { state: { returnUrl: `/detail/${id}` } });
      }
    });
  };

  const handleSendMessage = async () => {
    if (!messageText.trim()) return;

    setSendingMessage(true);
    setMessageStatus({ type: '', text: '' });

    try {
      let endpoint;
      let requestData;

      if (isTalentAuthenticated) {
        // Talent sending message to another talent
        endpoint = '/messages/talent/send';
        requestData = {
          recipientId: service?.worker?.id,
          recipientType: 'talent',
          subject: `Message about: ${service?.title}`,
          message: messageText.trim(),
          serviceId: id
        };
      } else {
        // Customer sending message to talent
        endpoint = '/messages/send';
        requestData = {
          recipientId: service?.worker?.id,
          recipientType: 'talent',
          subject: `Message about: ${service?.title}`,
          message: messageText.trim(),
          serviceId: id
        };
      }

      const { data } = await talentApi.post(endpoint, requestData);

      console.log('Message response:', data);
      if (data.success) {
        const successMessage = isTalentAuthenticated
          ? 'Message sent to fellow talent successfully!'
          : 'Message sent successfully!';
        setMessageStatus({ type: 'success', text: successMessage });
        setMessageText('');
        setTimeout(() => {
          setShowContactModal(false);
          setMessageStatus({ type: '', text: '' });
        }, 2000);
      } else {
        setMessageStatus({ type: 'error', text: data?.message || 'Failed to send message' });
      }
    } catch (error: any) {
      console.error('Error sending message:', error);
      setMessageStatus({
        type: 'error',
        text: error.response?.data?.message || 'Failed to send message. Please try again.'
      });
    } finally {
      setSendingMessage(false);
    }
  };

  const handleCallProvider = async () => {
    // Track contact click
    if (service?.worker?.id) {
      try {
        await talentApi.post(`/talent/track-contact/${service.worker.id}`, {
          contactType: 'phone'
        });
        console.log('Phone click tracked');
      } catch (trackingError) {
        console.error('Error tracking phone click:', trackingError);
      }
    }

    if (service?.worker?.phone) {
      window.open(`tel:${service.worker.phone}`, '_self');
    }
  };

  const fetchReviews = async () => {
    if (!id) return;

    try {
      setReviewsLoading(true);
      // Use central api utility
      const data = await api.get(`/services/${id}/reviews?limit=10&page=1`);

      if (data.success) {
        setReviewData(data);
      } else {
        console.error('Failed to fetch reviews:', data.message);
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setReviewsLoading(false);
    }
  };

  const handleShare = async () => {
    // Track share click
    if (service?.worker?.id) {
      try {
        await talentApi.post(`/talent/track-share/${service.worker.id}`);
        console.log('Share click tracked');
      } catch (trackingError) {
        console.error('Error tracking share click:', trackingError);
      }
    }

    const currentUrl = window.location.href;
    const serviceTitle = service?.title || 'Amazing Service';
    const serviceDescription = service?.description || 'Check out this amazing service!';

    // Create share data
    const shareData = {
      title: serviceTitle,
      text: serviceDescription,
      url: currentUrl
    };

    // Check if native Web Share API is supported
    if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
      navigator.share(shareData)
        .then(() => console.log('Shared successfully'))
        .catch((error) => {
          console.log('Error sharing:', error);
          // Fallback to manual share options
          showShareOptions(currentUrl, serviceTitle, serviceDescription);
        });
    } else {
      // Fallback for browsers that don't support Web Share API
      showShareOptions(currentUrl, serviceTitle, serviceDescription);
    }
  };

  const showShareOptions = (url: string, title: string, description: string) => {
    // Create share URLs for different platforms
    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(`${title} - ${url}`)}`,
      telegram: `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
      email: `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`${description}\n\n${url}`)}`
    };

    // Create share options HTML
    const shareOptionsHTML = `
      <div class="share-modal" style="
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0,0,0,0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 9999;
      ">
        <div class="share-content" style="
          background: white;
          padding: 30px;
          border-radius: 10px;
          max-width: 400px;
          width: 90%;
          text-align: center;
        ">
          <h4 style="margin-bottom: 20px; color: #333;">Share this service</h4>
          <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 15px; margin-bottom: 20px;">
            <a href="${shareUrls.facebook}" target="_blank" rel="noopener noreferrer" style="
              display: flex;
              align-items: center;
              justify-content: center;
              padding: 12px;
              background:rgb(0, 0, 0);
              color: white;
              text-decoration: none;
              border-radius: 8px;
              font-weight: bold;
            ">Facebook</a>
            <a href="${shareUrls.twitter}" target="_blank" rel="noopener noreferrer" style="
              display: flex;
              align-items: center;
              justify-content: center;
              padding: 12px;
              background:rgb(0, 0, 0);
              color: white;
              text-decoration: none;
              border-radius: 8px;
              font-weight: bold;
            ">Twitter</a>
            <a href="${shareUrls.linkedin}" target="_blank" rel="noopener noreferrer" style="
              display: flex;
              align-items: center;
              justify-content: center;
              padding: 12px;
              background:rgb(0, 0, 0);
              color: white;
              text-decoration: none;
              border-radius: 8px;
              font-weight: bold;
            ">LinkedIn</a>
            <a href="${shareUrls.whatsapp}" target="_blank" rel="noopener noreferrer" style="
              display: flex;
              align-items: center;
              justify-content: center;
              padding: 12px;
              background:rgb(0, 0, 0);
              color: white;
              text-decoration: none;
              border-radius: 8px;
              font-weight: bold;
            ">WhatsApp</a>
            <a href="${shareUrls.telegram}" target="_blank" rel="noopener noreferrer" style="
              display: flex;
              align-items: center;
              justify-content: center;
              padding: 12px;
              background:rgb(0, 0, 0);
              color: white;
              text-decoration: none;
              border-radius: 8px;
              font-weight: bold;
            ">Telegram</a>
            <a href="${shareUrls.email}" style="
              display: flex;
              align-items: center;
              justify-content: center;
              padding: 12px;
              background:rgb(0, 0, 0);
              color: white;
              text-decoration: none;
              border-radius: 8px;
              font-weight: bold;
            ">Email</a>
          </div>
          <div style="margin-bottom: 20px;">
            <label style="display: block; margin-bottom: 8px; font-weight: bold;">Or copy link:</label>
            <div style="display: flex; gap: 10px;">
              <input type="text" value="${url}" readonly style="
                flex: 1;
                padding: 10px;
                border: 1px solid #ddd;
                border-radius: 5px;
                font-size: 14px;
              ">
              <button onclick="navigator.clipboard.writeText('${url}').then(() => this.textContent = 'Copied!').catch(() => this.textContent = 'Failed')" style="
                padding: 10px 15px;
                background:rgb(0, 0, 0);
                color: white;
                border: none;
                border-radius: 5px;
                cursor: pointer;
                font-weight: bold;
              ">Copy</button>
            </div>
          </div>
          <button onclick="this.closest('.share-modal').remove()" style="
            padding: 10px 20px;
            background:rgb(0, 0, 0);
            color: white;
            border: none;
            border-radius: 5px;
            cursor: pointer;
            font-weight: bold;
          ">Close</button>
        </div>
      </div>
    `;

    // Create and append the modal
    const modalContainer = document.createElement('div');
    modalContainer.innerHTML = shareOptionsHTML;
    document.body.appendChild(modalContainer);

    // Add click outside to close
    modalContainer.querySelector('.share-modal')?.addEventListener('click', (e) => {
      if (e.target === e.currentTarget) {
        modalContainer.remove();
      }
    });
  };

  const handleBookmark = () => {
    const currentUrl = window.location.href;
    const serviceTitle = service?.title || 'Amazing Service';

    // Show bookmark options modal
    showBookmarkOptions(currentUrl, serviceTitle);
  };

  const showBookmarkOptions = (url: string, title: string) => {
    // Create bookmark options HTML
    const bookmarkOptionsHTML = `
      <div class="bookmark-modal" style="
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0,0,0,0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 9999;
      ">
        <div class="bookmark-content" style="
          background: white;
          padding: 30px;
          border-radius: 10px;
          max-width: 400px;
          width: 90%;
          text-align: center;
        ">
          <h4 style="margin-bottom: 20px; color: #333;">Bookmark this page</h4>
          <p style="margin-bottom: 20px; color: #666;">Choose how you'd like to bookmark this page:</p>
          
          <div style="display: flex; flex-direction: column; gap: 15px; margin-bottom: 20px;">
            <button onclick="window.open('${url}', '_blank')" style="
              display: flex;
              align-items: center;
              justify-content: center;
              padding: 12px;
              background: rgb(0, 0, 0);
              color: white;
              text-decoration: none;
              border-radius: 8px;
              font-weight: bold;
              border: none;
              cursor: pointer;
              width: 100%;
            ">Open in New Tab</button>
            
            <button onclick="window.location.href='${url}'" style="
              display: flex;
              align-items: center;
              justify-content: center;
              padding: 12px;
              background: rgb(0, 0, 0);
              color: white;
              text-decoration: none;
              border-radius: 8px;
              font-weight: bold;
              border: none;
              cursor: pointer;
              width: 100%;
            ">Navigate to Page</button>
            
            <button onclick="navigator.clipboard.writeText('${url}').then(() => this.textContent = 'URL Copied!').catch(() => this.textContent = 'Failed to copy')" style="
              display: flex;
              align-items: center;
              justify-content: center;
              padding: 12px;
              background: rgb(0, 0, 0);
              color: white;
              text-decoration: none;
              border-radius: 8px;
              font-weight: bold;
              border: none;
              cursor: pointer;
              width: 100%;
            ">Copy URL</button>
          </div>
          
          <div style="margin-bottom: 20px;">
            <label style="display: block; margin-bottom: 8px; font-weight: bold;">Manual bookmark:</label>
            <p style="font-size: 14px; color: #666; margin-bottom: 10px;">
              Press <strong>Ctrl+D</strong> (Windows) or <strong>Cmd+D</strong> (Mac) to add this page to your bookmarks.
            </p>
            <div style="display: flex; gap: 10px;">
              <input type="text" value="${url}" readonly style="
                flex: 1;
                padding: 10px;
                border: 1px solid #ddd;
                border-radius: 5px;
                font-size: 14px;
              ">
              <button onclick="navigator.clipboard.writeText('${url}').then(() => this.textContent = 'Copied!').catch(() => this.textContent = 'Failed')" style="
                padding: 10px 15px;
                background: rgb(0, 0, 0);
                color: white;
                border: none;
                border-radius: 5px;
                cursor: pointer;
                font-weight: bold;
              ">Copy</button>
            </div>
          </div>
          
          <button onclick="this.closest('.bookmark-modal').remove()" style="
            padding: 10px 20px;
            background: rgb(0, 0, 0);
            color: white;
            border: none;
            border-radius: 5px;
            cursor: pointer;
            font-weight: bold;
          ">Close</button>
        </div>
      </div>
    `;

    // Create and append the modal
    const modalContainer = document.createElement('div');
    modalContainer.innerHTML = bookmarkOptionsHTML;
    document.body.appendChild(modalContainer);

    // Add click outside to close
    modalContainer.querySelector('.bookmark-modal')?.addEventListener('click', (e) => {
      if (e.target === e.currentTarget) {
        modalContainer.remove();
      }
    });
  };

  const showBookmarkSuccess = (message: string) => {
    // Create success notification HTML
    const successHTML = `
      <div class="bookmark-success" style="
        position: fixed;
        top: 20px;
        right: 20px;
        background: #28a745;
        color: white;
        padding: 15px 20px;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        z-index: 10000;
        animation: slideIn 0.3s ease;
      ">
        <div style="display: flex; align-items: center; gap: 10px;">
          <span style="font-size: 20px;">✓</span>
          <span>${message}</span>
        </div>
      </div>
      <style>
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      </style>
    `;

    // Create and append the notification
    const notificationContainer = document.createElement('div');
    notificationContainer.innerHTML = successHTML;
    document.body.appendChild(notificationContainer);

    // Auto-remove after 3 seconds
    setTimeout(() => {
      notificationContainer.remove();
    }, 3000);
  };

  const handleWriteReview = () => {
    console.log('handleWriteReview called, isAuthenticated:', isAuthenticated);

    if (!isAuthenticated) {
      console.log('Customer not authenticated, redirecting to login');
      navigate('/login', { state: { returnUrl: `/detail/${id}` } });
      return;
    }

    console.log('Customer authenticated, showing review modal');
    setShowReviewModal(true);
  };

  const reviewerName = isTalentAuthenticated
    ? `${talent?.firstName || ''} ${talent?.lastName || ''}`.trim()
    : `${customer?.firstName || ''} ${customer?.lastName || ''}`.trim();
  const submitReview = async (rating: number, reviewText: string, ratingCategories: {
    communication?: number;
    punctuality?: number;
    cost?: number;
    quality?: number;
  } = {}) => {
    if (!service || !isAuthenticated) return;

    try {
      setReviewSubmitting(true);

      // Debug: Check authentication state


      // Debug: Check if token exists
      const token = localStorage.getItem('skillseek_customer_token');
      console.log('Customer token exists:', !!token);
      if (token) {
        console.log('Token preview:', token.substring(0, 20) + '...');
      }

      // Use central api utility for customer reviews
      const data = await api.post('/customers/reviews', {
        
        revieweeId: service.worker.id,
        reviewer_name: reviewerName,
        serviceId: Number(id),
        rating: rating,
        communicationRating: ratingCategories.communication,
        punctualityRating: ratingCategories.punctuality,
        costRating: ratingCategories.cost,
        qualityRating: ratingCategories.quality,
        reviewText: reviewText
      });
      console.log('📥 Review API response:', data);

      if (data.success) {
        showReviewSuccess('Review submitted successfully!');
        setShowReviewModal(false);
        // Refresh reviews
        fetchReviews();
      } else {
        throw new Error(data.message || 'Failed to submit review');
      }
    } catch (error: any) {
      console.error('Error submitting review:', error);

      // Extract error message from API response
      let errorMessage = 'Error submitting review. Please try again.';

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      // Show error with SweetAlert2
      Swal.fire({
        icon: 'error',
        title: 'Review Submission Failed',
        text: errorMessage,
        confirmButtonText: 'Try Again',
        confirmButtonColor: '#FD2692',
        showClass: {
          popup: 'animate__animated animate__fadeInDown'
        },
        hideClass: {
          popup: 'animate__animated animate__fadeOutUp'
        }
      });
    } finally {
      setReviewSubmitting(false);
    }
  };

  // Component ke andar add karo (fetchService ke saath)
  const navigateToService = async (serviceTitle: string) => {
    try {
      const response = await talentApi.get(`/services?workerId=${service?.worker.id}`);
      const data = response.data;

      if (data.success && data.services) {
        const matched = data.services.find(
          (s: any) => s.title.toLowerCase().trim() === serviceTitle.toLowerCase().trim()
        );

        if (matched) {
          navigate(`/detail/${matched.id}`);
        } else {
          // Title se exact match nahi mila, worker ke listing pe le jao
          navigate(`/listing?worker=${service?.worker.id}`);
        }
      }
    } catch (error) {
      console.error('Error navigating to service:', error);
      navigate(`/listing?worker=${service?.worker.id}`);
    }
  };

  const showReviewSuccess = (message: string) => {
    const successHTML = `
      <div class="review-success" style="
        position: fixed;
        top: 20px;
        right: 20px;
        background: #28a745;
        color: white;
        padding: 15px 20px;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        z-index: 10000;
        animation: slideIn 0.3s ease;
      ">
        <div style="display: flex; align-items: center; gap: 10px;">
          <span style="font-size: 20px;">✓</span>
          <span>${message}</span>
        </div>
      </div>
      <style>
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      </style>
    `;

    const notificationContainer = document.createElement('div');
    notificationContainer.innerHTML = successHTML;
    document.body.appendChild(notificationContainer);

    setTimeout(() => {
      notificationContainer.remove();
    }, 3000);
  };

  const [activeTab, setActiveTab] = useState<'about' | 'reviews' | 'location'>('about');

  if (loading) {
    return (
      <div>
        {isCustomerAuthenticated ? <CustomerHeader /> : <Header />}
        <div className="container py-5">
          <div className="text-center">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Loading service details...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !service) {
    return (
      <div>
        {isCustomerAuthenticated ? <CustomerHeader /> : <Header />}
        <div className="container py-5">
          <div className="alert alert-danger text-center">
            <div className="mb-3">
              <AlertCircle size={48} className="text-danger" />
            </div>
            <h4>{error && error.includes('404') ? 'Service Not Found' : 'Error Loading Service'}</h4>
            <p className="my-3">{error || 'The requested service could not be found.'}</p>
            <div className="d-flex justify-content-center gap-3">
              <button
                className="btn btn-primary"
                onClick={() => navigate('/')}
              >
                Back to Home
              </button>
              {!error?.includes('404') && (
                <button
                  className="btn btn-outline-primary"
                  onClick={() => fetchService()}
                >
                  Try Again
                </button>
              )}
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div>
      <style>{`
        /* ── Detail Page (dp-) Styles ── */
        .dp-cover-wrap { width: 100%; height: 240px; overflow: hidden; background: #E5E7EB; position: relative; }
        .dp-cover-img { width: 100%; height: 100%; object-fit: cover; display: block; }
        .dp-page-body { max-width: 960px; margin: 0 auto; padding: 0 20px 60px; position: relative; top: -60px; }
        .dp-card { background: #fff; border-radius: 14px; box-shadow: 0 2px 12px rgba(0,0,0,0.08); padding: 28px; margin-bottom: 20px; }
        .dp-profile-card { padding-top: 56px; position: relative; }
        .dp-avatar-wrap { position: absolute; top: -52px; left: 28px; width: 104px; height: 104px; border-radius: 50%; border: 4px solid #fff; overflow: hidden; background: #f3f4f6; box-shadow: 0 2px 12px rgba(0,0,0,0.15); }
        .dp-avatar { width: 100%; height: 100%; object-fit: cover; }
        .dp-profile-top { display: flex; gap: 20px; align-items: flex-start; flex-wrap: wrap; }
        .dp-profile-meta { flex: 1; min-width: 200px; }
        .dp-profile-name-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 8px; }
        .dp-name { font-size: 22px; font-weight: 700; color: #111827; margin: 0; }
        .dp-badges { display: flex; gap: 6px; flex-wrap: wrap; align-items: center; }
        .dp-meta-row { display: flex; gap: 14px; flex-wrap: wrap; align-items: center; }
        .dp-meta-item { display: flex; align-items: center; gap: 5px; font-size: 13px; color: #6B7280; }
        .dp-rating-badge { color: #111827; font-weight: 600; }
        .dp-actions { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 16px; }
        .dp-btn { padding: 8px 16px; border-radius: 8px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.2s; border: 1.5px solid #E5E7EB; background: #fff; color: #374151; text-decoration: none; display: inline-flex; align-items: center; gap: 5px; }
        .dp-btn:hover { border-color: #185FA5; color: #185FA5; background: #E6F1FB; transform: translateY(-1px); }
        .dp-btn-primary { background: linear-gradient(135deg, #185FA5, #0E3A61); color: #fff; border: none; }
        .dp-btn-primary:hover { background: linear-gradient(135deg, #0E3A61, #185FA5); opacity: 1; transform: translateY(-1px); box-shadow: 0 4px 12px rgba(24, 95, 165, 0.2); color: #fff; }
        .dp-btn-primary:active { transform: translateY(0); opacity: 0.9; }
        .dp-btn-pill { border-radius: 20px; padding: 7px 16px; font-weight: 500; }
        .dp-btn-outline { border-color: #D1D5DB; }
        .dp-btn-ghost { background: #F9FAFB; border-color: #E5E7EB; }
        .dp-contact-strip { display: flex; gap: 20px; flex-wrap: wrap; margin-top: 20px; padding-top: 18px; border-top: 1px solid #F3F4F6; }
        .dp-contact-item { display: flex; align-items: center; gap: 7px; font-size: 13px; color: #6B7280; }
        .dp-card-title { font-size: 17px; font-weight: 700; color: #111827; margin: 0 0 18px; display: flex; align-items: center; gap: 8px; }
        .dp-card-count { font-size: 13px; font-weight: 500; color: #9CA3AF; background: #F3F4F6; padding: 2px 8px; border-radius: 20px; }
        .dp-grid-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
        .dp-media-cell { height: 200px; border-radius: 10px; overflow: hidden; background: #F9FAFB; }
        .dp-tab-card { padding: 0; overflow: hidden; }
        .dp-tab-nav { display: flex; border-bottom: 1.5px solid #F3F4F6; padding: 0 20px; }
        .dp-tab-btn { padding: 14px 20px; border: none; background: none; font-size: 14px; font-weight: 600; color: #9CA3AF; cursor: pointer; border-bottom: 2.5px solid transparent; margin-bottom: -1.5px; transition: color 0.2s; }
        .dp-tab-btn:hover { color: #374151; }
        .dp-tab-btn--active { color: #185FA5; border-bottom-color: #185FA5; }
        .dp-tab-content { padding: 24px; }
        .dp-section-title { font-size: 16px; font-weight: 700; color: #111827; margin: 0 0 14px; }
        .dp-description { font-size: 14px; color: #6B7280; line-height: 1.75; margin: 0 0 20px; }
        .dp-stats-row { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin: 20px 0; }
        .dp-stat-box { background: #F9FAFB; border-radius: 10px; padding: 16px; text-align: center; }
        .dp-stat-num { display: block; font-size: 22px; font-weight: 700; color: #111827; }
        .dp-stat-label { display: block; font-size: 12px; color: #9CA3AF; margin-top: 4px; }
        .dp-cat-ratings { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 20px; }
        .dp-cat-card { background: #F9FAFB; border-radius: 10px; padding: 16px; text-align: center; }
        .dp-cat-label { font-size: 12px; font-weight: 600; margin-bottom: 8px; }
        .dp-cat-stars { font-size: 16px; margin-bottom: 4px; }
        .dp-cat-num { font-size: 18px; font-weight: 700; color: #111827; }
        .dp-overall-rating { display: flex; gap: 24px; align-items: center; background: #F9FAFB; border-radius: 12px; padding: 20px; margin-bottom: 24px; }
        .dp-overall-score { text-align: center; flex-shrink: 0; }
        .dp-big-score { font-size: 40px; font-weight: 800; color: #111827; line-height: 1; }
        .dp-big-stars { font-size: 20px; margin: 4px 0; }
        .dp-rating-bars { flex: 1; }
        .dp-bar-row { display: flex; align-items: center; gap: 10px; margin-bottom: 6px; }
        .dp-bar-label { font-size: 12px; color: #6B7280; width: 24px; flex-shrink: 0; }
        .dp-bar-track { flex: 1; background: #E5E7EB; border-radius: 99px; height: 8px; }
        .dp-bar-fill { height: 100%; border-radius: 99px; transition: width 0.4s; }
        .dp-bar-count { font-size: 12px; color: #9CA3AF; width: 20px; text-align: right; flex-shrink: 0; }
        .dp-review-card { padding: 18px; border: 1px solid #F3F4F6; border-radius: 10px; margin-bottom: 14px; transition: box-shadow 0.2s; }
        .dp-review-card:hover { box-shadow: 0 4px 14px rgba(0,0,0,0.07); }
        .dp-review-head { display: flex; gap: 14px; align-items: flex-start; margin-bottom: 12px; }
        .dp-review-text { font-size: 14px; color: #4B5563; line-height: 1.7; margin: 0; padding: 12px; background: #F9FAFB; border-radius: 8px; border-left: 3px solid #0A67F5; }
        .dp-response-box { margin-top: 12px; padding: 12px 14px; background: #F0FDF4; border-radius: 8px; border: 1px solid #D1FAE5; }
        .dp-map-wrap { border-radius: 10px; overflow: hidden; border: 1px solid #E5E7EB; }
        .dp-map-footer { padding: 14px 16px; background: #F9FAFB; border-top: 1px solid #E5E7EB; }
        .dp-directions-link { margin-left: 14px; font-size: 13px; color: #0A67F5; text-decoration: none; font-weight: 500; }
        .dp-location-fallback { padding: 20px; background: #F9FAFB; border-radius: 10px; border: 1px solid #E5E7EB; }
        .services-slider::-webkit-scrollbar { height: 6px; }
        .services-slider::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 4px; }
        .services-slider::-webkit-scrollbar-thumb { background: #CBD5E1; border-radius: 4px; }
        .swal2-container { z-index: 999999 !important; }
        @media (max-width: 768px) {
          .dp-page-body { top: -20px; padding: 0 14px 40px; }
          .dp-cover-wrap { height: 160px; }
          .dp-grid-3 { grid-template-columns: repeat(2, 1fr); }
          .dp-cat-ratings { grid-template-columns: repeat(2, 1fr); }
          .dp-stats-row { grid-template-columns: repeat(2, 1fr); }
          .dp-overall-rating { flex-direction: column; }
          .dp-tab-btn { padding: 12px 12px; font-size: 12px; }
          .dp-name { font-size: 18px; }
          .dp-avatar-wrap { width: 80px; height: 80px; top: -40px; }
          .dp-profile-card { padding-top: 44px; }
        }
        @media (max-width: 480px) {
          .dp-grid-3 { grid-template-columns: 1fr 1fr; }
          .dp-stats-row { grid-template-columns: repeat(2, 1fr); }
          .dp-cat-ratings { grid-template-columns: repeat(2, 1fr); }
        }
      `}</style>

      {isCustomerAuthenticated ? <CustomerHeader /> : <Header />}

      {/* ── COVER PHOTO ── */}
      <div className="dp-cover-wrap">
        <img
          src={normalizeImageUrl(service.worker.coverImage || service.image, "/assets/images/cover.jpg")}
          alt={service.title}
          className="dp-cover-img"
          onError={(e) => { e.currentTarget.src = "/assets/images/cover.jpg"; }}
        />
      </div>

      {/* ── PAGE BODY ── */}
      <div className="dp-page-body">

        {/* PROFILE CARD */}
        <div className="dp-card dp-profile-card">
          <div className="dp-avatar-wrap">
            <img
              src={normalizeImageUrl(service.worker.profilePicture, "/assets/images/brand.png")}
              alt={service.worker.name}
              className="dp-avatar"
              onError={(e) => { e.currentTarget.src = "/assets/images/brand.png"; }}
            />
          </div>

          <div className="dp-profile-top">
            <div className="dp-profile-meta">
              <div className="dp-profile-name-row">
                <h1 className="dp-name">{service.worker.firstName} {service.worker.lastName}</h1>
                <div className="dp-badges">
                  <VerifiedBadge verificationStatus={service.worker.verificationStatus} />
                  <MembershipTierBadge tier={service.worker.membershipTier} />
                </div>
              </div>
              <div className="dp-meta-row">
                <span className="dp-meta-item">
                  <img src="/assets/images/loc.png" alt="Location" width={14} height={14} />
                  {service.worker.parish}{service.worker.city ? `, ${service.worker.city}` : ''}
                </span>
                <span className="dp-meta-item">⏱ {service.worker.experienceYears} yrs experience</span>
                <span className="dp-meta-item">⚡ High Response · 24hrs</span>
                {service.worker.rating > 0 && (
                  <span className="dp-meta-item dp-rating-badge">
                    <img src="/assets/images/star.png" alt="★" width={13} height={13} />
                    {service.worker.rating.toFixed(1)}
                    <span style={{ color: '#9CA3AF', fontWeight: 400 }}>({service.worker.totalReviews})</span>
                  </span>
                )}
              </div>

              {service.worker.phoneVisible && service.worker.phone && (
                <div className="dp-meta-row" style={{ marginTop: '6px' }}>
                  <span className="dp-meta-item">
                    <Phone size={14} className="text-success" />
                    {isAuthenticated
                      ? <a href={`tel:${service.worker.phone}`} style={{ color: '#16a34a', fontWeight: 600 }}>{service.worker.phone}</a>
                      : <span style={{ color: '#16a34a', fontWeight: 600 }}>{maskPhone(service.worker.phone)}</span>
                    }
                  </span>
                </div>
              )}

              <div className="dp-actions">
                <button className="dp-btn dp-btn-primary" onClick={handleContactRequest}>💬 Contact</button>
                <button className="dp-btn dp-btn-outline" onClick={(e) => { e.preventDefault(); handleWriteReview(); }}>⭐ Review</button>
                <a className="dp-btn dp-btn-ghost" href="#dp-gallery">📷 Photos</a>
                <button className="dp-btn dp-btn-ghost" onClick={(e) => { e.preventDefault(); handleShare(); }}>🔗 Share</button>
                <button className="dp-btn dp-btn-ghost" onClick={(e) => { e.preventDefault(); handleBookmark(); }}>🔖 Save</button>
              </div>
            </div>
          </div>

          {/* Contact info strip */}
          <div className="dp-contact-strip">
            <span className="dp-contact-item">
              <img src="/assets/images/envelop.png" alt="Email" width={16} height={16} />
              {isAuthenticated ? cleanEmail(service.worker.email) : maskEmail(cleanEmail(service.worker.email))}
            </span>
            <span className="dp-contact-item">
              <img src="/assets/images/call.png" alt="Phone" width={16} height={16} />
              {isAuthenticated ? service.worker.phone : maskPhone(service.worker.phone || '')}
            </span>
            {service.worker.address && (
              <span className="dp-contact-item">
                <img src="/assets/images/direction.png" alt="Address" width={16} height={16} />
                {service.worker.address}
              </span>
            )}
          </div>
        </div>

        {/* PORTFOLIO CARD */}
        {service.portfolio && service.portfolio.length > 0 && (
          <div className="dp-card" id="portfolio">
            <h2 className="dp-card-title">Portfolio <span className="dp-card-count">{service.portfolio.length} items</span></h2>
            <div className="dp-grid-3">
              {service.portfolio.map((item, index) => {
                const isVideo = ['.mp4', '.mov', '.avi', '.webm', '.mkv'].some(ext => item.toLowerCase().endsWith(ext));
                return (
                  <div key={index} className="dp-media-cell">
                    {isVideo
                      ? <video src={normalizeImageUrl(item, '')} controls style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : <img src={normalizeImageUrl(item, '/assets/images/sp-one.png')} alt={`Portfolio ${index + 1}`} onError={(e) => { e.currentTarget.src = "/assets/images/sp-one.png"; }} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    }
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* GALLERY CARD */}
        {service.galleryImages && service.galleryImages.length > 0 && (
          <div className="dp-card" id="dp-gallery">
            <h2 className="dp-card-title">Photos &amp; Videos <span className="dp-card-count">{service.galleryImages.length} photos</span></h2>
            <div className="dp-grid-3">
              {service.galleryImages.slice(0, 6).map((image, index) => (
                <div key={index} className="dp-media-cell">
                  <img src={normalizeImageUrl(image, '/assets/images/sp-one.png')} alt={`Gallery ${index + 1}`} onError={(e) => { e.currentTarget.src = "/assets/images/sp-one.png"; }} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* TABBED CARD: About | Reviews | Location */}
        <div className="dp-card dp-tab-card">
          <div className="dp-tab-nav">
            {(['about', 'reviews', 'location'] as const).map(tab => (
              <button
                key={tab}
                className={`dp-tab-btn${activeTab === tab ? ' dp-tab-btn--active' : ''}`}
                onClick={() => setActiveTab(tab)}
              >
                {tab === 'about' && '📋 About'}
                {tab === 'reviews' && `⭐ Reviews${reviewData ? ` (${reviewData.totalReviews})` : ''}`}
                {tab === 'location' && '📍 Location'}
              </button>
            ))}
          </div>

          {/* ABOUT TAB */}
          {activeTab === 'about' && (
            <div className="dp-tab-content">
              <h3 className="dp-section-title">About this Service</h3>
              <p className="dp-description">{service.description}</p>
              <div className="dp-stats-row">
                <div className="dp-stat-box"><span className="dp-stat-num">{service.worker.experienceYears}</span><span className="dp-stat-label">Years Active</span></div>
                <div className="dp-stat-box"><span className="dp-stat-num">{service.worker.totalJobsCompleted}</span><span className="dp-stat-label">Jobs Done</span></div>
                <div className="dp-stat-box"><span className="dp-stat-num">{service.worker.rating > 0 ? service.worker.rating.toFixed(1) : 'N/A'}</span><span className="dp-stat-label">Rating</span></div>
                <div className="dp-stat-box"><span className="dp-stat-num">95%</span><span className="dp-stat-label">Response Rate</span></div>
              </div>
              {service.worker.bio && (
                <div style={{ marginTop: '20px' }}>
                  <h4 style={{ fontSize: '15px', fontWeight: 600, color: '#1F2937', marginBottom: '8px' }}>Bio</h4>
                  <p style={{ fontSize: '14px', color: '#6B7280', lineHeight: '1.7' }}>{service.worker.bio}</p>
                </div>
              )}
            </div>
          )}

          {/* REVIEWS TAB */}
          {activeTab === 'reviews' && (
            <div className="dp-tab-content" id="reviews">
              {reviewsLoading ? (
                <div className="text-center py-4">
                  <div className="spinner-border" role="status"><span className="visually-hidden">Loading reviews...</span></div>
                  <p className="mt-2">Loading reviews...</p>
                </div>
              ) : reviewData ? (
                <>
                  {/* Category Ratings */}
                  <div className="dp-cat-ratings">
                    {[
                      { label: 'Communication', key: 'communication', color: '#3B82F6' },
                      { label: 'Quality', key: 'quality', color: '#10B981' },
                      { label: 'Punctuality', key: 'punctuality', color: '#6366F1' },
                      { label: 'Cost Value', key: 'cost', color: '#F59E0B' },
                    ].map(({ label, key, color }) => {
                      const avg = reviewData.categoryAverages?.[key as keyof typeof reviewData.categoryAverages] || 0;
                      return (
                        <div key={key} className="dp-cat-card">
                          <div className="dp-cat-label" style={{ color }}>{label}</div>
                          <div className="dp-cat-stars">{[1,2,3,4,5].map(s => <span key={s} style={{ color: s <= avg ? '#FBBF24' : '#E5E7EB', fontSize: '16px' }}>★</span>)}</div>
                          <div className="dp-cat-num">{avg > 0 ? avg : 'N/A'}</div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Overall Rating */}
                  <div className="dp-overall-rating">
                    <div className="dp-overall-score">
                      <div className="dp-big-score">{reviewData.totalReviews > 0 ? service.worker.rating.toFixed(1) : 'N/A'}</div>
                      <div className="dp-big-stars">{[1,2,3,4,5].map(s => <span key={s} style={{ color: reviewData.totalReviews > 0 && s <= Math.round(service.worker.rating) ? '#FBBF24' : '#E5E7EB', fontSize: '20px' }}>★</span>)}</div>
                      <div style={{ fontSize: '13px', color: '#9CA3AF' }}>{reviewData.totalReviews} reviews</div>
                    </div>
                    <div className="dp-rating-bars">
                      {[5,4,3,2,1].map(r => (
                        <div key={r} className="dp-bar-row">
                          <span className="dp-bar-label">{r}★</span>
                          <div className="dp-bar-track"><div className="dp-bar-fill" style={{ width: `${reviewData.distributionPercentages[r] || 0}%`, backgroundColor: r >= 4 ? '#10B981' : r >= 3 ? '#FBBF24' : '#EF4444' }} /></div>
                          <span className="dp-bar-count">{reviewData.distribution[r] || 0}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Write Review CTA */}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
                    <button className="dp-btn dp-btn-primary" onClick={handleWriteReview}>⭐ Write a Review</button>
                  </div>

                  {/* Review cards */}
                  {reviewData.reviews.length > 0 ? reviewData.reviews.map((review) => (
                    <div key={review.id} className="dp-review-card">
                      <div className="dp-review-head">
                        <span style={{ position: 'relative', flexShrink: 0 }}>
                          <img src={review.reviewerPicture} alt={review.reviewerName} onError={(e) => { e.currentTarget.src = "/assets/images/user.png"; }} style={{ width: '48px', height: '48px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #F3F4F6' }} />
                          {review.isVerified && <span style={{ position: 'absolute', bottom: '-2px', right: '-2px', background: '#10B981', color: 'white', borderRadius: '50%', width: '16px', height: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', border: '1.5px solid white' }}>✓</span>}
                        </span>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <strong style={{ fontSize: '15px', color: '#111827' }}>{review.reviewerName}</strong>
                            {review.isVerified && <span style={{ background: '#D1FAE5', color: '#065F46', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 500 }}>✓ Verified</span>}
                          </div>
                          <div style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '2px', textTransform: 'capitalize' }}>
                            {review.reviewerType === 'client' ? 'Client' : 'Customer'} · {new Date(review.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                          </div>
                          <div style={{ marginTop: '4px' }}>{[1,2,3,4,5].map(s => <span key={s} style={{ color: s <= review.rating ? '#FBBF24' : '#E5E7EB', fontSize: '14px' }}>★</span>)}</div>
                        </div>
                      </div>
                      <p className="dp-review-text">{review.reviewText}</p>
                      {review.responseText && (
                        <div className="dp-response-box">
                          <strong style={{ fontSize: '13px', color: '#374151' }}>Response from {service.worker.name}:</strong>
                          <p style={{ margin: '6px 0 0', fontSize: '13px', color: '#6B7280' }}>{review.responseText}</p>
                        </div>
                      )}
                    </div>
                  )) : (
                    <div className="text-center py-4"><p style={{ color: '#9CA3AF' }}>No reviews yet. Be the first to review this service!</p></div>
                  )}

                  {reviewData.pagination.totalPages > 1 && (
                    <div className="d-flex justify-content-center mt-4">
                      <nav><ul className="pagination">
                        {Array.from({ length: reviewData.pagination.totalPages }, (_, i) => i + 1).map((page) => (
                          <li key={page} className={`page-item ${page === reviewData.pagination.currentPage ? 'active' : ''}`}>
                            <button className="page-link" onClick={() => fetchReviews()}>{page}</button>
                          </li>
                        ))}
                      </ul></nav>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-4"><p style={{ color: '#9CA3AF' }}>No review data available.</p></div>
              )}
            </div>
          )}

          {/* LOCATION TAB */}
          {activeTab === 'location' && (
            <div className="dp-tab-content">
              <h3 className="dp-section-title">Service Location</h3>

              {!isAuthenticated && service?.worker?.parish && (
                <div className="dp-map-wrap">
                  <ServiceMap 
                    parish={service.worker.parish} 
                    city={service.worker.city} 
                    height="260px" 
                    isAuthenticated={isAuthenticated}
                    onRestrictedAction={handleRestrictedLocationAction}
                  />
                  <div className="dp-map-footer">
                    <p style={{ margin: 0, fontSize: '14px', color: '#374151' }}>
                      Works from: <strong>{service.worker.parish}</strong>
                      <a 
                        href="#" 
                        onClick={(e) => {
                          e.preventDefault();
                          handleRestrictedLocationAction();
                        }} 
                        className="dp-directions-link"
                      >
                        🧭 Get Directions
                      </a>
                    </p>
                    <p style={{ margin: '6px 0 0', fontSize: '13px', color: '#9CA3AF' }}>
                      <a href="/customer/login" style={{ color: '#3B82F6' }}>Sign in</a> to see exact location
                    </p>
                  </div>
                </div>
              )}

              {isAuthenticated && service?.worker?.latitude && service?.worker?.longitude && (
                <div className="dp-map-wrap">
                  <ServiceMap 
                    latitude={service.worker.latitude} 
                    longitude={service.worker.longitude} 
                    workerName={`${service.worker.firstName} ${service.worker.lastName}`} 
                    address={service.worker.address} 
                    isAuthenticated={isAuthenticated}
                    onRestrictedAction={handleRestrictedLocationAction}
                  />
                  {service.worker.address && (
                    <div className="dp-map-footer">
                      <p style={{ margin: 0, fontSize: '14px', color: '#374151', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                        <span><strong>Address:</strong> {service.worker.address}</span>
                        <a href={`https://www.google.com/maps/dir/?api=1&destination=${service.worker.latitude},${service.worker.longitude}`} target="_blank" rel="noopener noreferrer" className="dp-directions-link">🧭 Get Directions</a>
                      </p>
                    </div>
                  )}
                </div>
              )}

              {isAuthenticated && (!service?.worker?.latitude || !service?.worker?.longitude) && (service?.worker?.address || service?.worker?.parish) && (
                <div className="dp-map-wrap">
                  <ServiceMap 
                    address={service.worker.address} 
                    parish={service.worker.parish} 
                    city={service.worker.city} 
                    height="260px" 
                    isAuthenticated={isAuthenticated}
                    onRestrictedAction={handleRestrictedLocationAction}
                  />
                  <div className="dp-map-footer">
                    <p style={{ margin: 0, fontSize: '14px', color: '#374151', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                      <span>Works from: <strong>{service.worker.address || service.worker.parish}</strong></span>
                      <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(service.worker.address || service.worker.parish || '')}`} target="_blank" rel="noopener noreferrer" className="dp-directions-link">🧭 Get Directions</a>
                    </p>
                    <p style={{ margin: '6px 0 0', fontSize: '13px', color: '#9CA3AF' }}>
                      Exact coordinates not available. Showing general area.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* OTHER SERVICES CARD */}
        <div className="dp-card">
          <h2 className="dp-card-title">Other Services by {service.worker.firstName}</h2>
          <div style={{ position: 'relative', overflow: 'hidden', background: '#F9FAFB', borderRadius: '10px', padding: '16px', border: '1px solid #E5E7EB' }}>
            <div className="services-slider" style={{ display: 'flex', gap: '15px', overflowX: 'auto', scrollBehavior: 'smooth', paddingBottom: '10px', scrollbarWidth: 'thin', scrollbarColor: '#CBD5E1 #F9FAFB' }}>
              {service.worker.services && service.worker.services.length > 0 ? (
                service.worker.services.map((serviceItem: WorkerService, index: number) => (
                  <div
                    key={index}
                    className="recent-serv-inner"
                    style={{ minWidth: '260px', background: 'white', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 1px 6px rgba(0,0,0,0.08)', transition: 'transform 0.2s ease, box-shadow 0.2s ease', cursor: 'pointer', flexShrink: 0, border: '1px solid #E5E7EB' }}
                    onClick={() => navigate(`/detail/${serviceItem.id}`)}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.12)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 1px 6px rgba(0,0,0,0.08)'; }}
                  >
                    <img
                      className="service-image"
                      width={260} height={160}
                      src={normalizeImageUrl(serviceItem.image || service.worker.profilePicture, '/assets/images/service-one.png')}
                      onError={(e) => { (e.target as HTMLImageElement).src = '/assets/images/service-one.png'; }}
                      alt={serviceItem.title}
                      style={{ width: '100%', height: '160px', objectFit: 'cover' }}
                    />
                    <div style={{ padding: '12px' }}>
                      <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '6px', color: '#111827', lineHeight: '1.3' }}>{serviceItem.title}</h4>
                      <p style={{ margin: 0, fontSize: '13px', color: '#6B7280', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        From ${serviceItem.price.toFixed(2)}
                        <span style={{ display: 'flex', alignItems: 'center', gap: '3px', color: '#FBBF24', fontWeight: 500 }}>
                          <img src="/assets/images/star.png" width={12} height={12} alt="" />
                          {service.worker.rating.toFixed(1)}
                        </span>
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <div style={{ textAlign: 'center', padding: '32px', color: '#9CA3AF', width: '100%' }}>
                  <h5 style={{ color: '#9CA3AF', marginBottom: '6px' }}>No additional services</h5>
                  <p style={{ margin: 0, fontSize: '13px' }}>Only offers: "{service.title}"</p>
                </div>
              )}
            </div>
            {service.worker.services && service.worker.services.length > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '12px' }}>
                <button onClick={() => { const s = document.querySelector('.services-slider') as HTMLElement; if (s) s.scrollBy({ left: -280, behavior: 'smooth' }); }} className="dp-btn dp-btn-primary dp-btn-pill">← Prev</button>
                <button onClick={() => { const s = document.querySelector('.services-slider') as HTMLElement; if (s) s.scrollBy({ left: 280, behavior: 'smooth' }); }} className="dp-btn dp-btn-primary dp-btn-pill">Next →</button>
              </div>
            )}
            <div style={{ textAlign: 'center', marginTop: '8px', color: '#9CA3AF', fontSize: '13px' }}>
              {service.worker.services?.length || 0} service{(service.worker.services?.length || 0) !== 1 ? 's' : ''} available
            </div>
          </div>
        </div>

      </div>{/* /dp-page-body */}

      <Newsletter />
      <Footer />

      {/* Contact Modal */}
      {showContactModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', padding: '30px', borderRadius: '10px', maxWidth: '500px', width: '90%', maxHeight: '80vh', overflowY: 'auto' }}>
            <div style={{ marginBottom: '20px' }}>
              <h4 style={{ margin: '0 0 10px 0', color: '#333' }}>{isTalentAuthenticated ? 'Connect with Fellow Talent' : 'Contact'} {service?.worker?.firstName} {service?.worker?.lastName}</h4>
              <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>{isTalentAuthenticated ? `Connect with ${service?.worker?.firstName} about: ` : "Choose how you'd like to contact the service provider about: "}<strong>{service?.title}</strong></p>
            </div>
            <div style={{ marginBottom: '20px', display: 'flex', gap: '15px', justifyContent: 'center' }}>
              <button onClick={handleCallProvider} style={{ padding: '12px 20px', background: '#28a745', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px', transition: 'background-color 0.2s' }} onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#218838'} onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#28a745'}><Phone size={18} />Call Now</button>
              <button onClick={() => { document.getElementById('messageForm')?.scrollIntoView({ behavior: 'smooth' }); }} style={{ padding: '12px 20px', background: '#007bff', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px', transition: 'background-color 0.2s' }} onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#0056b3'} onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#007bff'}>💬 Send Message</button>
            </div>
            <div id="messageForm" style={{ borderTop: '1px solid #eee', paddingTop: '20px' }}>
              <h5 style={{ margin: '0 0 15px 0', color: '#333' }}>{isTalentAuthenticated ? 'Send Message to Fellow Talent' : 'Send a Message'}</h5>
              {messageStatus.text && (
                <div style={{ padding: '10px 15px', borderRadius: '5px', marginBottom: '15px', backgroundColor: messageStatus.type === 'success' ? '#d4edda' : '#f8d7da', color: messageStatus.type === 'success' ? '#155724' : '#721c24', border: `1px solid ${messageStatus.type === 'success' ? '#c3e6cb' : '#f5c6cb'}` }}>{messageStatus.text}</div>
              )}
              <textarea value={messageText} onChange={(e) => setMessageText(e.target.value)} placeholder={isTalentAuthenticated ? "Connect with this talent about collaboration, questions, or networking..." : "Type your message here..."} style={{ width: '100%', minHeight: '100px', padding: '12px', border: '1px solid #ddd', borderRadius: '5px', resize: 'vertical', fontSize: '14px', marginBottom: '15px', fontFamily: 'inherit' }} />
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button onClick={() => { setShowContactModal(false); setMessageText(''); setMessageStatus({ type: '', text: '' }); }} style={{ padding: '10px 20px', background: '#6c757d', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold' }}>Cancel</button>
                <button onClick={handleSendMessage} disabled={sendingMessage || !messageText.trim()} style={{ padding: '10px 20px', background: (!messageText.trim() || sendingMessage) ? '#ccc' : '#007bff', color: 'white', border: 'none', borderRadius: '5px', cursor: (!messageText.trim() || sendingMessage) ? 'not-allowed' : 'pointer', fontWeight: 'bold', opacity: sendingMessage ? 0.6 : 1 }}>{sendingMessage ? 'Sending...' : 'Send'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Review Modal */}
      {showReviewModal && (
        <div className="review-modal" style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <ReviewForm serviceName={service?.title || ''} workerName={service?.worker.name || ''} onSubmit={submitReview} onClose={() => setShowReviewModal(false)} isSubmitting={reviewSubmitting} />
        </div>
      )}
    </div>
  )
}
interface ReviewFormProps {
  serviceName: string;
  workerName: string;
  onSubmit: (rating: number, reviewText: string, ratingCategories: {
    communication?: number;
    punctuality?: number;
    cost?: number;
    quality?: number;
  }) => void;
  onClose: () => void;
  isSubmitting: boolean;
}

const ReviewForm: React.FC<ReviewFormProps> = ({
  serviceName,
  workerName,
  onSubmit,
  onClose,
  isSubmitting
}) => {
  const [rating, setRating] = useState(0);
  const [communicationRating, setCommunicationRating] = useState(0);
  const [punctualityRating, setPunctualityRating] = useState(0);
  const [costRating, setCostRating] = useState(0);
  const [qualityRating, setQualityRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [hoveredRating, setHoveredRating] = useState(0);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Check if at least one rating category is selected
    const hasRating = rating > 0 || communicationRating > 0 || punctualityRating > 0 || costRating > 0 || qualityRating > 0;
    if (!hasRating) {
      Swal.fire({
        icon: 'warning',
        title: 'Rating Required',
        text: 'Please select at least one rating category',
        confirmButtonText: 'OK',
        confirmButtonColor: '#FD2692'
      });
      return;
    }

    if (reviewText.trim().length < 10) {
      Swal.fire({
        icon: 'warning',
        title: 'Review Too Short',
        text: 'Please write at least 10 characters for your review',
        confirmButtonText: 'OK',
        confirmButtonColor: '#FD2692'
      });
      return;
    }

    const ratingCategories = {
      communication: communicationRating > 0 ? communicationRating : undefined,
      punctuality: punctualityRating > 0 ? punctualityRating : undefined,
      cost: costRating > 0 ? costRating : undefined,
      quality: qualityRating > 0 ? qualityRating : undefined
    };

    onSubmit(rating, reviewText.trim(), ratingCategories);
  };

  // Check if at least one rating category is selected (for button state)
  const hasRating = rating > 0 || communicationRating > 0 || punctualityRating > 0 || costRating > 0 || qualityRating > 0;

  return (
    <div style={{
      background: 'white',
      padding: '30px',
      borderRadius: '10px',
      maxWidth: '500px',
      width: '90%',
      maxHeight: '80vh',
      overflowY: 'auto'
    }}>
      <div style={{ marginBottom: '20px' }}>
        <h4 style={{ margin: '0 0 10px 0', color: '#333' }}>Write a Review</h4>
        <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
          Share your experience with <strong>{workerName}</strong> for <strong>{serviceName}</strong>
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
            Overall Rating (Optional)
          </label>
          <div style={{ display: 'flex', gap: '5px', marginBottom: '10px' }}>
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: star <= (hoveredRating || rating) ? '#ffc107' : '#e4e5e9',
                  padding: '5px'
                }}
                onClick={() => setRating(star)}
                onMouseEnter={() => setHoveredRating(star)}
                onMouseLeave={() => setHoveredRating(0)}
              >
                ★
              </button>
            ))}
          </div>
          <small style={{ color: '#666' }}>
            {rating === 0 && 'Select an overall rating (optional)'}
            {rating === 1 && 'Poor'}
            {rating === 2 && 'Fair'}
            {rating === 3 && 'Good'}
            {rating === 4 && 'Very Good'}
            {rating === 5 && 'Excellent'}
          </small>
        </div>

        {/* Rating Categories */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '15px', fontWeight: 'bold', color: '#333' }}>
            Rate Specific Aspects *
          </label>

          {/* Communication Rating */}
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#555' }}>
              Communication
            </label>
            <div style={{ display: 'flex', gap: '3px' }}>
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '18px',
                    cursor: 'pointer',
                    color: star <= communicationRating ? '#ffc107' : '#e4e5e9',
                    padding: '3px'
                  }}
                  onClick={() => setCommunicationRating(star)}
                >
                  ★
                </button>
              ))}
            </div>
            <small style={{ color: '#666', fontSize: '12px' }}>
              {communicationRating === 0 && 'Rate communication quality'}
              {communicationRating > 0 && `${communicationRating} star${communicationRating !== 1 ? 's' : ''}`}
            </small>
          </div>

          {/* Punctuality Rating */}
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#555' }}>
              Punctuality
            </label>
            <div style={{ display: 'flex', gap: '3px' }}>
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '18px',
                    cursor: 'pointer',
                    color: star <= punctualityRating ? '#ffc107' : '#e4e5e9',
                    padding: '3px'
                  }}
                  onClick={() => setPunctualityRating(star)}
                >
                  ★
                </button>
              ))}
            </div>
            <small style={{ color: '#666', fontSize: '12px' }}>
              {punctualityRating === 0 && 'Rate timeliness'}
              {punctualityRating > 0 && `${punctualityRating} star${punctualityRating !== 1 ? 's' : ''}`}
            </small>
          </div>

          {/* Cost Rating */}
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#555' }}>
              Cost Value
            </label>
            <div style={{ display: 'flex', gap: '3px' }}>
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '18px',
                    cursor: 'pointer',
                    color: star <= costRating ? '#ffc107' : '#e4e5e9',
                    padding: '3px'
                  }}
                  onClick={() => setCostRating(star)}
                >
                  ★
                </button>
              ))}
            </div>
            <small style={{ color: '#666', fontSize: '12px' }}>
              {costRating === 0 && 'Rate value for money'}
              {costRating > 0 && `${costRating} star${costRating !== 1 ? 's' : ''}`}
            </small>
          </div>

          {/* Quality Rating */}
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#555' }}>
              Work Quality
            </label>
            <div style={{ display: 'flex', gap: '3px' }}>
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '18px',
                    cursor: 'pointer',
                    color: star <= qualityRating ? '#ffc107' : '#e4e5e9',
                    padding: '3px'
                  }}
                  onClick={() => setQualityRating(star)}
                >
                  ★
                </button>
              ))}
            </div>
            <small style={{ color: '#666', fontSize: '12px' }}>
              {qualityRating === 0 && 'Rate work quality'}
              {qualityRating > 0 && `${qualityRating} star${qualityRating !== 1 ? 's' : ''}`}
            </small>
          </div>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
            Your Review *
          </label>
          <textarea
            value={reviewText}
            onChange={(e) => setReviewText(e.target.value)}
            placeholder="Share your experience with this service provider. What did you like? What could be improved?"
            style={{
              width: '100%',
              minHeight: '120px',
              padding: '12px',
              border: '1px solid #ddd',
              borderRadius: '5px',
              fontSize: '14px',
              fontFamily: 'inherit',
              resize: 'vertical'
            }}
            maxLength={1000}
          />
          <small style={{ color: '#666' }}>
            {reviewText.length}/1000 characters (minimum 10 characters)
          </small>
        </div>

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            style={{
              padding: '12px 20px',
              background: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              fontWeight: 'bold',
              opacity: isSubmitting ? 0.6 : 1
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting || !hasRating || reviewText.trim().length < 10}
            style={{
              padding: '12px 20px',
              background: !hasRating || reviewText.trim().length < 10 ? '#ccc' : 'rgb(0, 0, 0)',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: (isSubmitting || !hasRating || reviewText.trim().length < 10) ? 'not-allowed' : 'pointer',
              fontWeight: 'bold',
              opacity: isSubmitting ? 0.6 : 1
            }}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Review'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Detail
